import { useMemo } from 'react';
import { useQuery } from 'urql';

import { gql } from '@/shared/types/gql';

/**
 * The tenant's per-module switches, as the server computes them.
 *
 * The server resolves a section's `enabled` from the tenant's feature flags
 * layered over its tier defaults (`{...TIER_DEFAULTS[tier], ...tenant.featureFlags}`),
 * and the permission resolver downgrades a disabled section to NO_ACCESS
 * regardless of what `role_module_permission` grants. So a module that is off
 * here will 403 on every query behind it — the sidebar must not offer it.
 *
 * This is why prod showed a Vault entry that answered "Couldn't load files":
 * `crm.vault` defaults to false in every tier and the investbanq tenant had no
 * override, so the section was disabled while the sidebar advertised it anyway.
 */
const NAVIGATION_CONFIG = gql`
  query NavigationConfig {
    navigation {
      config {
        crm {
          key
          enabled
          subsections {
            key
            enabled
          }
        }
      }
    }
  }
`;

interface NavigationSection {
  key: string;
  /**
   * Nullable on purpose: a partial GraphQL response can omit it, and only an
   * explicit `false` may hide an entry. Widening the type is what keeps the
   * `=== false` check below honest — `!enabled` would treat a missing value as
   * "switched off" and empty the sidebar on a partial response.
   */
  enabled?: boolean | null;
  subsections?: NavigationSection[] | null;
}

interface NavigationConfigData {
  navigation?: { config?: { crm?: NavigationSection[] | null } | null } | null;
}

/** Exported under a test-only name so the walk can be unit-tested directly. */
export { collectDisabled as collectDisabledForTest };

function collectDisabled(
  sections: NavigationSection[] | null | undefined,
  into: Set<string>,
): void {
  for (const section of sections ?? []) {
    if (section.enabled === false) into.add(section.key);
    collectDisabled(section.subsections, into);
  }
}

export interface ModuleFlags {
  /** Module keys the server reports as OFF, e.g. `crm.vault`. */
  disabled: ReadonlySet<string>;
  fetching: boolean;
}

/**
 * Fail-OPEN by design: only keys the server explicitly reports as
 * `enabled: false` land in the set. While the query is in flight, or if it
 * errors, the set is empty and every entry stays visible.
 *
 * The alternative — hiding until proven enabled — turns one failed request
 * into an empty sidebar, which reads as a broken app. Showing a module the
 * tenant cannot use is the milder failure, and it is the state we are already
 * in today.
 */
export function useModuleFlags(): ModuleFlags {
  const [{ data, fetching }] = useQuery<NavigationConfigData>({
    query: NAVIGATION_CONFIG,
    requestPolicy: 'cache-first',
  });

  const disabled = useMemo(() => {
    const out = new Set<string>();
    collectDisabled(data?.navigation?.config?.crm, out);
    return out;
  }, [data]);

  return { disabled, fetching };
}
